<template>
  <div class="schedule-row" :class="[schedule.eventType]" @click="onClick">
    <img class="schedule-row__flag" :src="require(`@/assets/images/flags/${schedule.country}.webp`)" alt="flag" />

    <div class="schedule-row__body">
      <div class="schedule-row__top">
        <span class="schedule-row__event-name">{{ schedule.eventName }}</span>
        <span v-if="schedule.matchDay" class="schedule-row__match-day">{{ schedule.matchDay.toUpperCase() }}</span>
        <span v-if="schedule.eventType === 'current'" class="schedule-row__live">{{ $t('LIVE') }}</span>
      </div>
      <div class="schedule-row__meta">
        <span class="schedule-row__date-time">
          {{
            renderDateTime(
              schedule.startFightDateTime ? schedule.startFightDateTime : schedule.matchStartDateTime,
              'date'
            )
          }}
          {{
            renderDateTime(
              schedule.startFightDateTime ? schedule.startFightDateTime : schedule.matchStartDateTime,
              'time'
            )
          }}
        </span>
        <span v-if="schedule.eventDescription" class="schedule-row__desc">{{ schedule.eventDescription }}</span>
      </div>
    </div>

    <div class="schedule-row__fights">
      <img
        :src="schedule.eventType === 'current' ? swordFightBlack : swordFightWhite"
        class="schedule-row__sword"
        alt=""
      />
      <span>{{ schedule.lastFightNumber ? schedule.lastFightNumber : schedule.expectedNumFights }}</span>
    </div>

    <div v-if="showLinkIcon && schedule.imageUrl" class="schedule-row__link">
      <img src="@/assets/images/arrow-square-up-right.png" alt="" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import swordFightBlack from '@/assets/images/sword-fight-black.webp';
import swordFightWhite from '@/assets/images/sword-fight-white.webp';

export default {
  props: {
    schedule: {
      type: Object,
      default: () => [],
    },
    showLinkIcon: {
      type: Boolean,
      default: true,
    },
    /*
     * The desktop ticker popover is 244px wide and the design puts the date and
     * the time on one line as `01/10 - 04:05`. Everywhere else keeps the full
     * `DD/MM/YYYY HH:mm:ss`.
     */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      swordFightBlack,
      swordFightWhite,
    };
  },
  computed: {
    ...mapGetters('general', ['timezone']),
  },
  methods: {
    onClick() {
      if (!this.schedule.imageUrl) return;
      this.$emit('goToDetails');
    },
    renderDateTime(value, action) {
      const newValue = new Date(value);

      switch (action) {
        case 'date':
          return this.$moment(newValue).tz(this.timezone).format(this.compact ? 'DD/MM -' : 'DD/MM/YYYY');
        case 'time':
          return this.$moment(newValue).tz(this.timezone).format(this.compact ? 'HH:mm' : 'HH:mm:ss');
        default:
          return;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  background-color: #0e2033;
  color: #75889b;
  cursor: default;

  &.current {
    cursor: pointer;
    border-color: rgba(234, 195, 90, 0.5);
    background-image: linear-gradient(to right, rgba(234, 195, 90, 0.14), rgba(14, 32, 51, 0));
  }

  &.future {
    cursor: pointer;
  }

  &.past {
    opacity: 0.55;
  }
}

.schedule-row__flag {
  flex: 0 0 auto;
  width: 40px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
  margin: 0;
}

.schedule-row__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.schedule-row__top {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.schedule-row__event-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
}

.schedule-row__match-day {
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: #19344e;
  color: #b2d6ea;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.schedule-row__live {
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: #eac35a;
  color: #1a1200;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.schedule-row__meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  color: #6a91a7;
}

.schedule-row__date-time {
  flex: 0 0 auto;
  white-space: nowrap;
}

.schedule-row__desc {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #75889b;

  &::before {
    content: '•';
    margin-right: 6px;
  }
}

.schedule-row__fights {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: #19344e;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.current .schedule-row__fights {
  background-image: linear-gradient(to bottom, #f4ea9f, #eac35a);
  color: #1a1200;
}

.schedule-row__sword {
  width: 12px;
  height: 12px;
  margin: 0;
}

.schedule-row__link {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  padding: 8px;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 3px 0 0 #a8a8a8;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 12px;
    height: 12px;
    margin: 0;
  }
}

.current .schedule-row__link {
  box-shadow: 0 3px 0 0 #653e10;
  background-image: linear-gradient(209deg, #f4ea9f 106%, #eac35a 18%);
}
</style>
